import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import GoogleSignInButton from "../../../../Components/Common/GoogleSignInButton";
import { mergeTenantMeIntoSession } from "../../../../helpers/api_helper";
import {
  deleteMeGoogle,
  postMeGoogleLink,
} from "../../../../helpers/fakebackend_helper";
import { isGoogleLoginEnabled } from "../../../../helpers/google_auth_helper";
import { fetchMe } from "../../../../helpers/profile_helper";

/**
 * Bloco Conta Google — vincular / desvincular (LAC-045).
 */
const AccountGoogleSection = () => {
  const { t } = useTranslation();
  const enabled = isGoogleLoginEnabled();
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await fetchMe();
      setLinked(!!me.user?.google_linked);
      setHasPassword(me.user?.has_password !== false);
      mergeTenantMeIntoSession(me);
    } catch {
      setError(t("settings.account.google.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleLink = async (idToken: string) => {
    setBusy(true);
    setError(null);
    try {
      const envelope: any = await postMeGoogleLink({ id_token: idToken });
      if (!envelope?.success) {
        setError(
          envelope?.error?.message || t("settings.account.google.linkError")
        );
        return;
      }
      setLinked(true);
      toast.success(t("settings.account.google.linkSuccess"));
      await load();
    } catch (err: any) {
      setError(err?.message || t("settings.account.google.linkError"));
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (!hasPassword) {
      setError(t("settings.account.google.unlinkNeedsPassword"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const envelope: any = await deleteMeGoogle();
      if (!envelope?.success) {
        setError(
          envelope?.error?.message || t("settings.account.google.unlinkError")
        );
        return;
      }
      setLinked(false);
      toast.success(t("settings.account.google.unlinkSuccess"));
      await load();
    } catch (err: any) {
      setError(err?.message || t("settings.account.google.unlinkError"));
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <Card className="mt-4" id="google-login">
      <CardHeader>
        <h5 className="card-title mb-0">{t("settings.account.google.title")}</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted mb-3">{t("settings.account.google.hint")}</p>
        {error ? (
          <Alert color="danger" className="py-2" role="alert">
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Spinner size="sm" />
        ) : linked ? (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Badge color="success" pill>
              {t("settings.account.google.linked")}
            </Badge>
            <Button
              color="soft-danger"
              size="sm"
              disabled={busy || !hasPassword}
              onClick={() => void handleUnlink()}
            >
              {busy ? <Spinner size="sm" /> : t("settings.account.google.unlink")}
            </Button>
            {!hasPassword ? (
              <span className="text-muted small">
                {t("settings.account.google.unlinkNeedsPassword")}
              </span>
            ) : null}
          </div>
        ) : (
          <GoogleSignInButton busy={busy} onCredential={handleLink} width={280} />
        )}
      </CardBody>
    </Card>
  );
};

export default AccountGoogleSection;
